import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type {
  ListRoutingBreakersResponse,
  ResetRoutingBreakerRequest,
  ResetRoutingBreakerResponse,
  RoutingBreakerSettingsResponse,
  TripRoutingBreakerRequest,
  TripRoutingBreakerResponse,
  UpdateRoutingBreakerSettingsRequest,
  UpdateRoutingBreakerSettingsResponse,
} from '@nadoutong/contracts';
import { useSession } from '../hooks/useSession.tsx';
import {
  getRoutingBreakerSettings,
  listRoutingBreakers,
  resetRoutingBreaker,
  tripRoutingBreaker,
  updateRoutingBreakerSettings,
} from './routingBreakers.ts';

const ROUTING_BREAKERS_KEY = ['routing-breakers'] as const;
const ROUTING_BREAKER_SETTINGS_KEY = ['routing-breaker-settings'] as const;

/** Poll cadence: breaker state is short-lived, so refresh every ~15s. */
const ROUTING_BREAKERS_REFETCH_INTERVAL_MS = 15_000;

export function useRoutingBreakers(): UseQueryResult<ListRoutingBreakersResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ROUTING_BREAKERS_KEY,
    queryFn: ({ signal }) => listRoutingBreakers(fetchImpl, signal),
    refetchInterval: ROUTING_BREAKERS_REFETCH_INTERVAL_MS,
  });
}

export function useResetRoutingBreaker(): UseMutationResult<
  ResetRoutingBreakerResponse,
  Error,
  ResetRoutingBreakerRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => resetRoutingBreaker(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROUTING_BREAKERS_KEY });
    },
  });
}

export function useTripRoutingBreaker(): UseMutationResult<
  TripRoutingBreakerResponse,
  Error,
  TripRoutingBreakerRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => tripRoutingBreaker(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROUTING_BREAKERS_KEY });
    },
  });
}

export function useRoutingBreakerSettings(): UseQueryResult<RoutingBreakerSettingsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ROUTING_BREAKER_SETTINGS_KEY,
    queryFn: ({ signal }) => getRoutingBreakerSettings(fetchImpl, signal),
  });
}

/**
 * Threshold settings mutation (epic #886 settings PR). Invalidates both
 * the settings snapshot and the breakers list — an enable/threshold
 * change alters how the list endpoint reports cooldowns and states.
 */
export function useUpdateRoutingBreakerSettings(): UseMutationResult<
  UpdateRoutingBreakerSettingsResponse,
  Error,
  UpdateRoutingBreakerSettingsRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => updateRoutingBreakerSettings(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROUTING_BREAKER_SETTINGS_KEY });
      void qc.invalidateQueries({ queryKey: ROUTING_BREAKERS_KEY });
    },
  });
}
